// src/pages/Vendor/ApplicationStatus/index.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  Mail, 
  ArrowRight, 
  FileText,
  RefreshCw,
  Store,
  ArrowLeft
} from 'lucide-react';
import { vendorAPI } from '../../../api/vendor';
import { useAuth } from '../../../contexts/AuthContext';
import toast from '../../../utils/toast';

interface ApplicationStatusData {
  verification_status: 'pending' | 'approved' | 'rejected' | 'none';
  business_name?: string;
  business_email?: string;
  created_at?: string;
  updated_at?: string;
  rejection_reason?: string;
  message?: string;
}

const ApplicationStatus: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<ApplicationStatusData | null>(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await vendorAPI.getApplicationStatus();
      if (response.data) {
        setApplication(response.data);
      } else {
        setApplication({ verification_status: 'none', message: 'No application found' });
      }
    } catch (error: any) {
      toast.error('Failed to load application status');
      setApplication({ verification_status: 'none', message: 'Unable to load status' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    const configs = {
      pending: {
        icon: Clock,
        color: 'text-yellow-600',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        title: 'Application Under Review',
        message: 'Your application is being reviewed by our team. We\'ll notify you once a decision is made.',
        buttonText: 'Check Back Later',
        buttonAction: () => navigate('/products'),
        buttonIcon: ArrowLeft,
      },
      approved: {
        icon: CheckCircle,
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-green-200',
        title: 'Application Approved! 🎉',
        message: 'Congratulations! Your vendor account is now active. Start listing your products and growing your business.',
        buttonText: 'Go to Dashboard',
        buttonAction: () => navigate('/vendor/dashboard'),
        buttonIcon: ArrowRight,
      },
      rejected: {
        icon: XCircle,
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200',
        title: 'Application Not Approved',
        message: 'We appreciate your interest in becoming a vendor. Unfortunately, we cannot approve your application at this time.',
        buttonText: 'Contact Support',
        buttonAction: () => window.location.href = 'mailto:support@wrighttechdepot.com',
        buttonIcon: Mail,
      },
      none: {
        icon: FileText,
        color: 'text-gray-600',
        bg: 'bg-gray-50',
        border: 'border-gray-200',
        title: 'No Application Found',
        message: 'You haven\'t submitted a vendor application yet.',
        buttonText: 'Apply Now',
        buttonAction: () => navigate('/vendor/apply'),
        buttonIcon: Store,
      },
    };
    return configs[status as keyof typeof configs] || configs.none;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading application status...</p>
        </div>
      </div>
    );
  }

  const status = application?.verification_status || 'none';
  const config = getStatusConfig(status);
  const StatusIcon = config.icon;
  const ButtonIcon = config.buttonIcon;

  // Format dates
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          ← Back
        </button>

        {/* Status Card */}
        <div className={`${config.bg} border ${config.border} rounded-xl p-8 text-center mb-6`}>
          <div className={`w-16 h-16 rounded-full ${config.bg} border ${config.border} flex items-center justify-center mx-auto mb-4`}>
            <StatusIcon className={`h-8 w-8 ${config.color}`} />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">{config.title}</h2>
          <p className="text-gray-600 mt-2">{config.message}</p>
          
          {status === 'pending' && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-yellow-700 bg-yellow-100 px-4 py-2 rounded-full mx-auto max-w-fit">
              <Clock className="h-4 w-4" />
              <span>Estimated review: 1-3 business days</span>
            </div>
          )}
          
          {status === 'rejected' && application?.rejection_reason && (
            <div className="mt-4 text-left bg-red-100 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-medium text-red-800">Reason:</p>
              <p className="text-sm text-red-700">{application.rejection_reason}</p>
            </div>
          )}
        </div>

        {/* Application Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h3 className="font-medium text-gray-900">Application Details</h3>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Business Name</span>
              <span className="text-gray-900 font-medium">
                {application?.business_name || 'N/A'}
              </span>
            </div>
            
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Business Email</span>
              <span className="text-gray-900 font-medium">
                {application?.business_email || 'N/A'}
              </span>
            </div>
            
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Submitted</span>
              <span className="text-gray-900 font-medium">
                {formatDate(application?.created_at)}
              </span>
            </div>

            {application?.updated_at && application.created_at !== application.updated_at && (
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Last Updated</span>
                <span className="text-gray-900 font-medium">
                  {formatDate(application?.updated_at)}
                </span>
              </div>
            )}

            <div className="flex justify-between py-2">
              <span className="text-gray-500">Status</span>
              <span className={`font-medium capitalize ${
                status === 'pending' ? 'text-yellow-600' :
                status === 'approved' ? 'text-green-600' :
                status === 'rejected' ? 'text-red-600' :
                'text-gray-600'
              }`}>
                {status || 'Unknown'}
              </span>
            </div>
          </div>

          {/* Refresh Button */}
          <button
            onClick={fetchStatus}
            className="w-full mt-4 flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 py-2 border-t border-gray-100"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh Status
          </button>
        </div>

        {/* Action Button */}
        <div className="mt-6">
          <button
            onClick={config.buttonAction}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center justify-center gap-2"
          >
            <ButtonIcon className="h-4 w-4" />
            {config.buttonText}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-gray-500">
          {status === 'pending' && (
            <p>
              Need help? <a href="mailto:support@wrighttechdepot.com" className="text-blue-600 hover:underline">Contact support</a>
            </p>
          )}
          {status === 'approved' && (
            <p>
              Need assistance getting started? <a href="mailto:support@wrighttechdepot.com" className="text-blue-600 hover:underline">Contact support</a>
            </p>
          )}
          {status === 'rejected' && (
            <p>
              You can <button onClick={() => navigate('/vendor/apply')} className="text-blue-600 hover:underline">reapply</button> after addressing the issues above.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationStatus;